import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const BestShowsOrlandoPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const faqs = [
    {
      question: "What makes Mental Hop Scotch one of the best shows in Orlando?",
      answer: "Mental Hop Scotch stands out because it's not just a show you watch—it's an experience you participate in. Master mentalist Simon Nomis creates an intimate, interactive performance where audience members become the stars. Unlike large theme park shows, every seat offers an incredible view and personal connection."
    },
    {
      question: "How does it compare to theme park shows?",
      answer: "While theme park shows offer spectacle, Mental Hop Scotch offers intimacy and personal interaction. You won't find this level of audience participation or mind-bending mentalism at any theme park. It's a completely unique Orlando experience."
    },
    {
      question: "Is this suitable for tourists visiting Orlando?",
      answer: "Absolutely! Mental Hop Scotch is perfect for tourists looking for something beyond the typical Orlando attractions. It's a refreshing break from crowded parks and offers an authentic, local entertainment experience."
    },
    {
      question: "When and where is the show?",
      answer: "Shows are every Saturday at 7:00 PM in Orlando, FL. The intimate venue ensures every guest has an amazing experience."
    },
    {
      question: "How much are tickets?",
      answer: "Tickets range from $35 (Standard) to $70 (VIP). VIP includes meet & greet with Simon, front row seating, exclusive merchandise, and signed memorabilia."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Mental Hop Scotch - Best Shows in Orlando",
    "description": "Discover one of the best shows in Orlando. Mental Hop Scotch is an award-winning mental magic experience by Simon Nomis.",
    "url": "https://www.simonnomislive.com/best-shows-orlando",
    "image": "https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg",
    "performer": { "@type": "Person", "name": "Simon Nomis" },
    "location": { "@type": "Place", "name": "Orlando, FL", "address": { "@type": "PostalAddress", "addressLocality": "Orlando", "addressRegion": "FL" } },
    "offers": { "@type": "Offer", "price": "35", "priceCurrency": "USD", "url": "https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" }
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Best Shows in Orlando 2026 | Mental Hop Scotch | Simon Nomis Live</title>
        <meta name="description" content="Discover the best shows in Orlando! Mental Hop Scotch is an interactive magic experience that tops the list. Book tickets for Orlando's most talked-about live show." />
        <meta name="keywords" content="best shows in Orlando, Orlando shows, things to do Orlando, Orlando entertainment, best Orlando attractions, Orlando nightlife, live shows Orlando" />
        <link rel="canonical" href="https://www.simonnomislive.com/best-shows-orlando" />
        <meta property="og:title" content="Best Shows in Orlando 2026 | Mental Hop Scotch" />
        <meta property="og:description" content="Discover the best shows in Orlando! Mental Hop Scotch is an interactive magic experience." />
        <meta property="og:url" content="https://www.simonnomislive.com/best-shows-orlando" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-black via-purple-900/30 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6"><span className="text-purple-400">Best Shows</span> in Orlando</h1>
            <p className="text-xl md:text-2xl text-purple-300 mb-8">Experience Orlando's Most Talked-About Live Entertainment</p>
            <p className="text-lg text-gray-300 mb-10 leading-relaxed">
              Looking for the best shows Orlando has to offer? <strong className="text-purple-400">Mental Hop Scotch</strong> has quickly become one of the city's must-see attractions. This isn't your typical Orlando show—it's an immersive, mind-bending experience that leaves audiences speechless.
            </p>
            <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-10 py-4 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Get Tickets Now</a>
          </div>
        </div>
      </section>

      {/* Why Best */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center">Why <span className="text-purple-400">Mental Hop Scotch</span> Tops the List</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-lg font-bold text-purple-400 mb-2">100% Interactive</h3>
                <p className="text-gray-300 text-sm">Every show is unique based on audience participation</p>
              </div>
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">🧠</div>
                <h3 className="text-lg font-bold text-purple-400 mb-2">Mind-Blowing</h3>
                <p className="text-gray-300 text-sm">Mentalism that defies explanation</p>
              </div>
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">🎭</div>
                <h3 className="text-lg font-bold text-purple-400 mb-2">Intimate Venue</h3>
                <p className="text-gray-300 text-sm">Every seat is a great seat</p>
              </div>
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">⭐</div>
                <h3 className="text-lg font-bold text-purple-400 mb-2">Award-Winning</h3>
                <p className="text-gray-300 text-sm">Performed by master illusionist Simon Nomis</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Show Info */}
      <section className="py-16 bg-gradient-to-br from-purple-900/20 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">About <span className="text-purple-400">The Show</span></h2>
                <p className="text-gray-300 mb-4">Mental Hop Scotch is a 90-minute journey into the impossible. Simon Nomis doesn't just perform tricks—he reads minds, predicts the future, and reveals secrets that simply couldn't be known.</p>
                <p className="text-gray-300 mb-6">Audience members become the stars, walking away with cash from impossible predictions and props as souvenirs.</p>
                <div className="bg-gray-900/50 p-4 rounded-lg border border-purple-500/30">
                  <p className="text-white">📅 Saturdays at 7:00 PM</p>
                  <p className="text-white">📍 Orlando, FL</p>
                  <p className="text-white">💵 Tickets from $35</p>
                </div>
              </div>
              <img src="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" alt="Best Shows Orlando" className="rounded-lg shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center"><span className="text-purple-400">Frequently Asked</span> Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-900/50 rounded-lg border border-purple-500/30 overflow-hidden">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-purple-900/20 transition-colors">
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <span className="text-purple-400 text-2xl flex-shrink-0">{openFaq === index ? '−' : '+'}</span>
                  </button>
                  {openFaq === index && <div className="px-6 pb-4"><p className="text-gray-300 leading-relaxed">{faq.answer}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-purple-900/30 via-black to-purple-900/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">See Why It's <span className="text-purple-400">Orlando's Best</span></h2>
          <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-12 py-5 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Buy Tickets Now</a>
          <p className="text-gray-400 mt-6 text-sm">📍 Orlando, FL</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BestShowsOrlandoPage;